import React, { useEffect, useMemo, useState } from "react";
import { api, resolveMedia } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SectionTitle } from "@/components/SectionTitle";
import { PlayerProfileModal } from "@/components/PlayerProfileModal";
import { User } from "@phosphor-icons/react";

function TeamLogo({ team, size = 56 }) {
  return (
    <div
      className="flex items-center justify-center text-black font-display overflow-hidden shrink-0"
      style={{ width: size, height: size, background: team.primary_color || "#FF5722", fontSize: size * 0.42 }}
    >
      {team.logo_url ? <img src={team.logo_url} alt={team.name} className="w-full h-full object-cover" /> : team.name.charAt(0)}
    </div>
  );
}

export default function Teams() {
  const { settings } = useSettings();
  const DIVISIONS = useMemo(() => settings.divisions || [], [settings.divisions]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [division, setDivision] = useState("Open");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (DIVISIONS.length && !DIVISIONS.includes(division)) setDivision(DIVISIONS[0]);
  }, [DIVISIONS, division]);

  const divisionTeams = useMemo(
    () => teams.filter((t) => (t.division || "Open") === division),
    [teams, division]
  );

  const rosterOf = (teamId) => players.filter((p) => p.team_id === teamId);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="teams-page">
      <SectionTitle overline="The league" title="Teams" sub="Browse every team and roster, organized by division." />

      <div className="flex flex-wrap gap-2 mb-10">
        {DIVISIONS.map((d) => (
          <button
            key={d}
            data-testid={`teams-division-${d.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setDivision(d)}
            className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
              division === d ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      {divisionTeams.length === 0 && <p className="text-white/40">No teams in the {division} division yet.</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {divisionTeams.map((t) => {
          const roster = rosterOf(t.id);
          return (
            <div key={t.id} data-testid="team-public-card" className="border border-white/10 bg-[#121212] overflow-hidden">
              <div className="flex items-center gap-4 p-6 border-b border-white/10">
                <TeamLogo team={t} />
                <div className="min-w-0">
                  <div className="font-display text-2xl uppercase truncate">{t.name}</div>
                  <div className="text-white/50 text-sm">{t.coach ? `Coach ${t.coach}` : "—"} · {roster.length} players</div>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-wide bg-[#FF5722]/20 text-[#FF5722] px-2 py-1 self-start">{t.division || "Open"}</span>
              </div>
              <div>
                {roster.length === 0 && <p className="text-white/40 text-sm px-6 py-5">Roster coming soon.</p>}
                {roster.map((p) => (
                  <button key={p.id} type="button" onClick={() => setSelected({ player: p, team: t })} data-testid={`roster-player-${p.id}`} className="w-full flex items-center justify-between px-6 py-3 border-b border-white/5 last:border-0 text-left hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-display text-xl text-[#FF5722] tabular-nums w-8 shrink-0">{p.number != null ? `#${p.number}` : ""}</span>
                      <span className="w-9 h-9 bg-[#0A0A0A] border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                        {p.photo_url ? <img src={resolveMedia(p.photo_url)} alt={p.name} className="w-full h-full object-cover" /> : <User size={16} className="text-white/25" />}
                      </span>
                      <span className="font-semibold uppercase truncate">{p.name}</span>
                      {p.age != null && <span className="text-white/40 text-xs shrink-0">· {p.age}y</span>}
                      {!p.waiver_signed && <span className="text-[9px] uppercase tracking-wide font-bold bg-[#FFEA00]/20 text-[#FFEA00] px-2 py-0.5 shrink-0" title="Waiver not signed yet">Waiver Pending</span>}
                    </div>
                    <span className="text-white/40 text-sm uppercase tracking-wide shrink-0">{p.position || ""}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selected && <PlayerProfileModal player={selected.player} team={selected.team} onClose={() => setSelected(null)} />}
    </div>
  );
}
